// Singleton polling store for metrics / SQL / health sources.
//
// Why this exists: each hook in use-metrics.js used to live inside the
// route component, so navigating away unmounted the hook, cancelled its
// setTimeout chain, and dropped the accumulated history (the Monitor
// page's throughput sparkline reset to empty on every tab switch).
//
// Model: each unique (kind, sql, interval) combination is owned by one
// `Source`. Sources are created lazily on first subscribe and keep
// running for the lifetime of the tab — last-unsubscribe does NOT stop
// the loop, on purpose, so coming back to a page sees the same warm
// cache (and any ring-buffered history) it left behind.
//
// Components subscribe via `useSource(source)` which is a thin wrapper
// around React's `useSyncExternalStore`. Each tick produces a new
// snapshot object reference, so React re-renders on every successful
// poll; the snapshot also carries `history` (a ring buffer reference)
// for sources that opted in via `derive`.

import { useSyncExternalStore } from 'react';
import { KineDB } from '@kinedb/client';
import { parseMetrics, deriveRates } from './use-metrics-utils.js';
import { getToken, isAnonymous, subscribe as subscribeSession } from './auth-store.js';

const sources = new Map();

// B1338: the pollers follow the session. A poll that failed with the old
// credential (a stale token, or none behind the login form) keeps its error until
// its next tick — up to 15 s for the chrome's summary, which then reads
// `unreachable` next to a live socket. A change of the session polls at once.
let _sessionKey = sessionKey();
function sessionKey() {
  return getToken() ?? (isAnonymous() ? 'anonymous' : null);
}
function followSession() {
  subscribeSession(() => {
    const k = sessionKey();
    if (k === _sessionKey) return;
    _sessionKey = k;
    for (const s of sources.values()) s.poke();
  });
}
followSession();

/** Test-only: `auth.__resetForTests` drops every auth listener, this one too. */
export function __followSessionForTests() {
  _sessionKey = sessionKey();
  followSession();
}

class Source {
  constructor({ key, intervalMs, fetch, derive = null, historyMax = 60 }) {
    this.key = key;
    this.intervalMs = intervalMs;
    this._fetch = fetch;
    this._derive = derive;
    this._historyMax = historyMax;
    this.history = [];
    this.snapshot = { error: null, history: this.history };
    this._listeners = new Set();
    this._started = false;
    this._timer = null;
    this._inFlight = false;
    this._again = false;
    this.subscribe = this.subscribe.bind(this);
    this.getSnapshot = this.getSnapshot.bind(this);
  }

  _broadcast() {
    for (const cb of this._listeners) {
      try { cb(); } catch { /* listener error */ }
    }
  }

  _start() {
    if (this._started) return;
    this._started = true;
    this._tick();
  }

  // B1338: poll NOW, for example after a login. A tick in flight carries the old
  // credential, so it asks for one more tick at its end instead of a second
  // chain: there is only ever one timer or one tick per source.
  poke() {
    if (!this._started) return;
    if (this._inFlight) { this._again = true; return; }
    clearTimeout(this._timer);
    this._tick();
  }

  _tick() {
    const tick = async () => {
      this._inFlight = true;
      try {
        const next = await this._fetch(this.snapshot);
        const merged = { ...this.snapshot, ...next, error: null, history: this.history };
        if (this._derive) {
          const sample = this._derive(merged, this.snapshot);
          if (sample != null) {
            this.history.push(sample);
            if (this.history.length > this._historyMax) {
              this.history.splice(0, this.history.length - this._historyMax);
            }
          }
        }
        this.snapshot = merged;
      } catch (e) {
        this.snapshot = { ...this.snapshot, error: e.message || String(e), history: this.history };
      } finally {
        this._inFlight = false;
        this._broadcast();
        const again = this._again;
        this._again = false;
        this._timer = setTimeout(tick, again ? 0 : this.intervalMs);
      }
    };
    tick();
  }

  subscribe(cb) {
    this._listeners.add(cb);
    this._start();
    return () => { this._listeners.delete(cb); };
  }

  getSnapshot() {
    return this.snapshot;
  }
}

function getOrCreate(key, factory) {
  let s = sources.get(key);
  if (!s) {
    s = factory();
    sources.set(key, s);
  }
  return s;
}

export function useSource(source) {
  return useSyncExternalStore(source.subscribe, source.getSnapshot);
}

// ── source factories ──────────────────────────────────────────────
//
// Each factory returns a singleton keyed by (kind, baseUrl?, sql?, interval).
// Multiple consumers calling the same factory with the same arguments
// share the same polling loop and the same accumulated history.

// SHOW METRICS poller. The throughput-history sparkline reads from the
// same source (snap.history), so subscribing to `useMetrics` and
// `useThroughputHistory` does NOT spawn two HTTP loops.
export function metricsSource(baseUrl = '', intervalMs = 1500) {
  return getOrCreate(`metrics::${baseUrl}::${intervalMs}`, () => new Source({
    key: `metrics::${baseUrl}::${intervalMs}`,
    intervalMs,
    historyMax: 60,
    fetch: async (prev) => {
      const db = new KineDB(baseUrl);
      const r = await db.sql('SHOW METRICS');
      return {
        latest: parseMetrics(r.data),
        prev: prev.latest ?? null,
        sampledAt: Date.now(),
        prevSampledAt: prev.sampledAt ?? 0,
      };
    },
    derive: (snap) => {
      const r = deriveRates(snap.latest, snap.prev, snap.sampledAt, snap.prevSampledAt);
      if (!r) return null;
      return { reads: r.readsPerSec, writes: r.writesPerSec, t: snap.sampledAt };
    },
  }));
}

export function healthSource(baseUrl = '', intervalMs = 5000) {
  return getOrCreate(`health::${baseUrl}::${intervalMs}`, () => new Source({
    key: `health::${baseUrl}::${intervalMs}`,
    intervalMs,
    fetch: async () => {
      const db = new KineDB(baseUrl);
      return { health: await db.health() };
    },
  }));
}

export function sqlSource(sql, baseUrl = '', intervalMs = 5000) {
  return getOrCreate(`sql::${baseUrl}::${intervalMs}::${sql}`, () => new Source({
    key: `sql::${baseUrl}::${intervalMs}::${sql}`,
    intervalMs,
    fetch: async () => {
      const db = new KineDB(baseUrl);
      const r = await db.sql(sql);
      return { columns: r.columns ?? [], rows: r.data ?? [] };
    },
  }));
}

export function nodesSource(intervalMs = 5000) {
  return getOrCreate(`nodes::${intervalMs}`, () => new Source({
    key: `nodes::${intervalMs}`,
    intervalMs,
    fetch: async () => {
      const db = new KineDB();
      const r = await db.sql('SHOW NODES');
      const cols = r.columns ?? [];
      const idx = name => cols.indexOf(name);
      const nodes = (r.data ?? []).map(row => ({
        node:               row[idx('node')],
        node_id:            row[idx('node_id')],
        status:             row[idx('status')],
        rss_bytes:          row[idx('rss_bytes')],
        memory_total_bytes: row[idx('memory_total_bytes')],
        last_seen_us:       row[idx('last_seen_us')],
      }));
      return { nodes };
    },
  }));
}

export function clusterSqlSource(sql, intervalMs = 5000) {
  return getOrCreate(`cluster-sql::${intervalMs}::${sql}`, () => new Source({
    key: `cluster-sql::${intervalMs}::${sql}`,
    intervalMs,
    fetch: async () => {
      const db = new KineDB();
      const r = await db.sql(sql);
      const cols = r.columns ?? [];
      const data = r.data ?? [];
      const nodeIdx = cols.indexOf('node');
      const innerCols = nodeIdx >= 0
        ? cols.slice(0, nodeIdx).concat(cols.slice(nodeIdx + 1))
        : cols;
      const byNode = {};
      for (const row of data) {
        const node = nodeIdx >= 0 ? row[nodeIdx] : '(unknown)';
        const innerRow = nodeIdx >= 0
          ? row.slice(0, nodeIdx).concat(row.slice(nodeIdx + 1))
          : row;
        (byNode[node] ||= []).push(innerRow);
      }
      return { byNode, columns: innerCols, rows: data };
    },
  }));
}

export function clusterMetricsSource(intervalMs = 1500) {
  return getOrCreate(`cluster-metrics::${intervalMs}`, () => new Source({
    key: `cluster-metrics::${intervalMs}`,
    intervalMs,
    fetch: async (prev) => {
      const db = new KineDB();
      const r = await db.sql('SHOW METRICS ON ALL NODES');
      const cols = r.columns ?? [];
      const data = r.data ?? [];
      const nodeIdx = cols.indexOf('node');
      const keyIdx = cols.indexOf('key');
      const valIdx = cols.indexOf('value');
      const byNode = {};
      for (const row of data) {
        const node = nodeIdx >= 0 ? row[nodeIdx] : '(unknown)';
        const k = row[keyIdx];
        const v = row[valIdx];
        (byNode[node] ||= {})[k] = typeof v === 'number' ? v : parseFloat(v);
      }
      return {
        byNode,
        prevByNode: prev.byNode ?? null,
        sampledAt: Date.now(),
        prevSampledAt: prev.sampledAt ?? 0,
      };
    },
  }));
}

// SHOW S3 STATUS poller. Returns the parsed scalar fields the lag card
// needs (local/synced/lag/pending) plus the raw columns/rows; the
// snapshot's `history` is the lag-trend ring buffer.
export function s3StatusSource(intervalMs = 2000) {
  return getOrCreate(`s3-status::${intervalMs}`, () => new Source({
    key: `s3-status::${intervalMs}`,
    intervalMs,
    historyMax: 60,
    fetch: async () => {
      const db = new KineDB();
      const r = await db.sql('SHOW S3 STATUS');
      const cols = r.columns ?? [];
      const data = r.data ?? [];
      const isDisabled = !(cols.length > 1 && data.length);
      const idx = name => cols.indexOf(name);
      const row = data[0];
      return {
        columns: cols,
        rows: data,
        isDisabled,
        local:   isDisabled ? null : row[idx('local_txn')],
        synced:  isDisabled ? null : row[idx('synced_txn')],
        lag:     isDisabled ? null : row[idx('txn_lag')],
        pending: isDisabled ? null : row[idx('pending_segments')],
      };
    },
    derive: (snap) => {
      if (snap.lag == null || snap.synced == null) return null;
      return { t: Date.now(), lag: snap.lag, synced: snap.synced };
    },
  }));
}
